import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  ongoing: 'bg-amber-100 text-amber-800',
  upcoming: 'bg-blue-100 text-blue-800',
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('photos');
  
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await publicApi.getProject(id);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProject();
  }, [id]);
  
  const nextImage = () => {
    if (project?.images?.length) {
      setActiveImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };
  
  const prevImage = () => {
    if (project?.images?.length) {
      setActiveImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };
  
  if (loading) {
    return (
      <div className="pt-20 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="skeleton h-8 w-32 mb-8" />
          <div className="skeleton h-96 mb-8" />
          <div className="skeleton h-8 w-64 mb-4" />
          <div className="skeleton h-24" />
        </div>
      </div>
    );
  }
  
  if (!project) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4">Project Not Found</h1>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/projects">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </div>
    );
  }
  
  const activeImage = project.images?.[activeImageIndex];
  
  return (
    <div className="pt-20" data-testid="project-detail-page">
      {/* Back Link */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <Link 
          to="/projects" 
          className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors text-sm"
          data-testid="back-to-projects"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Link>
      </div>
      
      {/* Hero Image Gallery */}
      <section className="pb-12" data-testid="project-gallery">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Tabs for Photos/Videos */}
          {project.videos?.length > 0 && (
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setActiveTab('photos')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'photos' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                Photos ({project.images?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'videos' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                Videos ({project.videos?.length || 0})
              </button>
            </div>
          )}
          
          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="relative">
              {/* Main Image */}
              <motion.div 
                key={activeImageIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative aspect-[16/9] overflow-hidden bg-secondary"
              >
                <img 
                  src={activeImage?.url || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'} 
                  alt={activeImage?.caption || project.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Caption Badge */}
                {activeImage?.caption && (
                  <div className="absolute bottom-6 left-6">
                    <span className="bg-black/70 text-white px-4 py-2 text-sm font-medium rounded-full">
                      {activeImage.caption}
                    </span>
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {project.images?.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors"
                      data-testid="prev-image"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors"
                      data-testid="next-image"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </motion.div>
              
              {/* Caption */}
              {activeImage?.caption && (
                <p className="text-center text-muted-foreground text-sm mt-4">
                  {activeImage.caption}
                </p>
              )}
              
              {/* Thumbnails */}
              {project.images?.length > 1 && (
                <div className="flex justify-center gap-3 mt-6">
                  {project.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImageIndex(index)}
                      className={`w-20 h-14 overflow-hidden rounded transition-all ${
                        index === activeImageIndex 
                          ? 'ring-2 ring-primary ring-offset-2' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img 
                        src={img.url} 
                        alt={img.caption}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Videos Tab */}
          {activeTab === 'videos' && project.videos?.length > 0 && (
            <div className="space-y-6">
              {project.videos.map((video, index) => {
                // Resolve an embeddable URL even for legacy records saved with wrong type
                const rawUrl = video.url || '';
                const ytMatch = rawUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                const vmMatch = rawUrl.match(/(?:vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|manage\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)/);
                const isDirectFile = /\.(mp4|webm|mov|avi|m4v)(\?|$)/i.test(rawUrl);

                let embedSrc = null;
                if (video.type === 'youtube' || ytMatch) {
                  embedSrc = ytMatch
                    ? `https://www.youtube.com/embed/${ytMatch[1]}`
                    : rawUrl;
                } else if (video.type === 'vimeo' || vmMatch) {
                  embedSrc = vmMatch
                    ? `https://player.vimeo.com/video/${vmMatch[1]}`
                    : rawUrl;
                }

                return (
                  <div key={index} className="relative" data-testid={`project-video-${index}`}>
                    {embedSrc ? (
                      <div className="aspect-video bg-black rounded-lg overflow-hidden">
                        <iframe
                          src={embedSrc}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={video.caption || `Video ${index + 1}`}
                        />
                      </div>
                    ) : isDirectFile ? (
                      <video
                        controls
                        className="w-full aspect-video bg-black rounded-lg"
                        poster={video.thumbnail}
                      >
                        <source src={rawUrl} />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className="aspect-video bg-black rounded-lg flex items-center justify-center text-white/70 text-sm">
                        Unable to play this video URL.
                      </div>
                    )}
                    {video.caption && (
                      <p className="text-center text-muted-foreground text-sm mt-3">
                        {video.caption}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12 md:py-20" data-testid="project-details">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="md:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 text-xs font-medium uppercase tracking-wider rounded-full ${statusColors[project.status]}`}>
                    {project.status}
                  </span>
                  <span className="text-muted-foreground text-sm uppercase tracking-wider">
                    {(project.project_type || '').replace(/_/g, ' ')}
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="project-name">
                  {project.name}
                </h1>
                
                <div className="flex items-center text-muted-foreground gap-2 mb-8">
                  <MapPin className="w-5 h-5" />
                  <span className="text-lg">{project.location}</span>
                </div>
                
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            </div>
            
            {/* Sidebar */}
            <div className="md:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-secondary/50 p-8 sticky top-28"
              >
                <h3 className="font-semibold mb-6">Project Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-muted-foreground text-sm">Project Type</p>
                    <p className="font-medium capitalize">{(project.project_type || '').replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Location</p>
                    <p className="font-medium">{project.location}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-sm">Status</p>
                    <p className="font-medium capitalize">{project.status}</p>
                  </div>
                  {project.images?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground text-sm">Photos</p>
                      <p className="font-medium">{project.images.length} image{project.images.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
                
                <hr className="my-6 border-border" />
                
                <Button asChild className="w-full rounded-full" data-testid="enquire-project">
                  <Link to="/contact">
                    Enquire About This Project
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="project-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Interested in Similar Projects?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
              Contact us to discuss your property development needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/projects">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  View All Projects
                </Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/contact">
                  Get In Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
